/*
 * <vitesse-dashboard-share-dialog> — the board's Share sheet.
 *
 * Google-Docs-shaped: who (anyone with the link, or named emails), for how long
 * (presets up to the 30-day cap, or a custom moment), and whether the board's
 * buttons work. Creating a link shows it ONCE — the secret is never stored, so
 * it cannot be shown again. Below, the board's existing shares with revoke, per
 * share or per invited person.
 *
 * The scope is derived from the board's tiles here (share-scope.ts) and sent as
 * the share's ceiling; the same derivation runs on the viewer's side to build
 * the grant the tiles render under.
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '@vitesse/base-ui/i18n';
import { fmtDateTime } from '@vitesse/base-ui/format';
import { LocaleController } from '@vitesse/base-ui/i18n';
import { button, iconButton } from '@vitesse/base-ui/components/base-buttons';
import '@vitesse/base-ui/elements/vitesse-dialog';
import type { DashboardManager } from '../managers/dashboard-manager';
import type { ModelManager } from '../managers/model-manager';
import type { Dashboard } from '../../core/domain/dashboard';
import type { ShareListingWire } from '../../core/bridge/connect-client';
import { scopeFromTiles, SHARE_TTL_PRESETS, SHARE_MAX_TTL_MS } from '../../dashboards/runtime/share-scope';

type Audience = 'link' | 'invite';

@customElement('vitesse-dashboard-share-dialog')
export class VitesseDashboardShareDialog extends LitElement {
  protected createRenderRoot(): HTMLElement {
    return this;
  }

  @property({ type: Boolean }) open = false;
  @property({ attribute: false }) board?: Dashboard;
  @property({ attribute: false }) dashboards?: DashboardManager;
  @property({ attribute: false }) models?: ModelManager;

  @state() private audience: Audience = 'link';
  @state() private emails = '';
  @state() private ttl = 'day';
  @state() private customUntil = '';
  @state() private interaction = false;
  @state() private busy = false;
  @state() private error = '';
  /** The link just made, shown once. */
  @state() private freshLink = '';
  @state() private freshPath = '';
  @state() private copied = false;
  @state() private shares: ShareListingWire[] = [];

  constructor() {
    super();
    new LocaleController(this);
  }

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open) {
      this.freshLink = '';
      this.freshPath = '';
      this.error = '';
      void this.reload();
    }
  }

  private async reload(): Promise<void> {
    if (!this.dashboards || !this.board) return;
    try {
      this.shares = await this.dashboards.listShares(this.board.id);
    } catch {
      this.shares = [];
    }
  }

  private expiresAt(): number | null {
    if (this.ttl === 'custom') {
      const ms = Date.parse(this.customUntil);
      return Number.isFinite(ms) ? ms : null;
    }
    const preset = SHARE_TTL_PRESETS.find((p) => p.id === this.ttl);
    return preset ? Date.now() + preset.ms : null;
  }

  private async create(): Promise<void> {
    if (!this.dashboards || !this.board || this.busy) return;
    const expiresAtMs = this.expiresAt();
    if (expiresAtMs === null || expiresAtMs <= Date.now() || expiresAtMs > Date.now() + SHARE_MAX_TTL_MS) {
      this.error = t('dashboards.shareExpiryInvalid');
      return;
    }
    const emails = this.emails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (this.audience === 'invite' && emails.length === 0) {
      this.error = t('dashboards.shareEmailsNeeded');
      return;
    }
    this.busy = true;
    this.error = '';
    try {
      const scope = scopeFromTiles(this.board.tiles, (mid) => this.models?.get(mid)?.commands ?? []);
      const r = await this.dashboards.share(this.board.id, {
        audience: this.audience,
        emails: this.audience === 'invite' ? emails : [],
        expiresAtMs,
        allowInteraction: this.audience === 'invite' && this.interaction,
      }, scope);
      if (!r.ok) {
        this.error = r.error;
        return;
      }
      this.freshPath = r.path;
      this.freshLink = r.linkToken ? `${location.origin}${r.path}#${r.linkToken.split('.').pop() ?? ''}` : `${location.origin}${r.path}`;
      this.emails = '';
      await this.reload();
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.busy = false;
    }
  }

  private async revoke(shareId: string, email = ''): Promise<void> {
    if (!this.dashboards || this.busy) return;
    this.busy = true;
    try {
      const r = await this.dashboards.revokeShare(shareId, email);
      if (!r.ok) this.error = r.error;
      await this.reload();
    } finally {
      this.busy = false;
    }
  }

  private async copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.copied = true;
      setTimeout(() => (this.copied = false), 1500);
    } catch {
      /* the field is selectable; copying by hand still works */
    }
  }

  private renderTerms() {
    const invite = this.audience === 'invite';
    return html`
      <div class="share-audience">
        ${(['link', 'invite'] as Audience[]).map(
          (a) => html`<label class="share-choice ${this.audience === a ? 'is-on' : ''}">
            <input type="radio" name="share-audience" .checked=${this.audience === a} @change=${() => (this.audience = a)} />
            <span class="share-choice-title">${t(a === 'link' ? 'dashboards.shareLink' : 'dashboards.shareInvite')}</span>
            <span class="share-choice-help">${t(a === 'link' ? 'dashboards.shareLinkHelp' : 'dashboards.shareInviteHelp')}</span>
          </label>`,
        )}
      </div>
      ${invite
        ? html`<label class="share-field">
            <span>${t('dashboards.shareEmails')}</span>
            <textarea class="config-input share-emails" rows="3" placeholder="ops@example.com, lead@example.com" .value=${this.emails} @input=${(e: Event) => (this.emails = (e.target as HTMLTextAreaElement).value)}></textarea>
          </label>`
        : nothing}
      <div class="share-row">
        <label class="share-field">
          <span>${t('dashboards.shareExpiry')}</span>
          <select class="config-select" .value=${this.ttl} @change=${(e: Event) => (this.ttl = (e.target as HTMLSelectElement).value)}>
            ${SHARE_TTL_PRESETS.map((p) => html`<option value=${p.id}>${t(`dashboards.shareTtl_${p.id}`)}</option>`)}
            <option value="custom">${t('dashboards.shareTtl_custom')}</option>
          </select>
        </label>
        ${this.ttl === 'custom'
          ? html`<label class="share-field">
              <span>${t('dashboards.shareUntil')}</span>
              <input class="config-input" type="datetime-local" .value=${this.customUntil} @input=${(e: Event) => (this.customUntil = (e.target as HTMLInputElement).value)} />
            </label>`
          : nothing}
      </div>
      ${invite
        ? html`<label class="share-toggle">
            <input type="checkbox" .checked=${this.interaction} @change=${(e: Event) => (this.interaction = (e.target as HTMLInputElement).checked)} />
            <span>${t('dashboards.shareInteraction')}</span>
            <span class="share-choice-help">${t('dashboards.shareInteractionHelp')}</span>
          </label>`
        : html`<p class="share-note">${t('dashboards.shareLinkReadOnly')}</p>`}
    `;
  }

  private renderFresh() {
    if (!this.freshPath) return nothing;
    return html`<div class="share-fresh">
      <div class="share-fresh-title">${t(this.audience === 'link' ? 'dashboards.shareLinkReady' : 'dashboards.shareInviteReady')}</div>
      <div class="share-fresh-row">
        <input class="config-input share-fresh-link" readonly .value=${this.freshLink} @focus=${(e: Event) => (e.target as HTMLInputElement).select()} />
        ${button({ label: this.copied ? t('dashboards.shareCopied') : t('dashboards.shareCopy'), icon: 'copy', color: 'blue', size: 'sm', onClick: () => void this.copy(this.freshLink) })}
      </div>
      ${this.audience === 'link' ? html`<p class="share-note">${t('dashboards.shareLinkOnce')}</p>` : html`<p class="share-note">${t('dashboards.shareInviteSend')}</p>`}
    </div>`;
  }

  private renderExisting() {
    const live = this.shares.filter((s) => !s.revokedAtMs && (!s.terms.expiresAtMs || s.terms.expiresAtMs > Date.now()));
    if (live.length === 0) return html`<p class="share-note">${t('dashboards.shareNone')}</p>`;
    const fmt = (ms: number) => fmtDateTime(ms);
    return html`<ul class="share-list">
      ${live.map(
        (s) => html`<li class="share-item">
          <div class="share-item-head">
            <span class="share-item-kind">${t(s.terms.audience === 'link' ? 'dashboards.shareLink' : 'dashboards.shareInvite')}</span>
            <span class="share-item-meta">${t('dashboards.shareExpires', { when: fmt(s.terms.expiresAtMs) })}${s.terms.allowInteraction ? ` · ${t('dashboards.shareCanAct')}` : ''}</span>
            ${iconButton({ icon: 'trash-2', title: t('dashboards.shareRevoke'), size: 14, danger: true, onClick: () => void this.revoke(s.id) })}
          </div>
          ${s.terms.audience === 'invite'
            ? html`<ul class="share-people">
                ${s.terms.emails.map(
                  (e) => html`<li>
                    <span>${e}</span>
                    ${iconButton({ icon: 'x', title: t('dashboards.shareRevokePerson'), size: 12, danger: true, onClick: () => void this.revoke(s.id, e) })}
                  </li>`,
                )}
              </ul>`
            : nothing}
        </li>`,
      )}
    </ul>`;
  }

  render() {
    return html`<vitesse-dialog .open=${this.open} heading=${t('dashboards.shareHeading', { title: this.board?.title ?? '' })} closeLabel=${t('dashboards.shareClose')} width="560px" @dialog-close=${() => this.dispatchEvent(new CustomEvent('dialog-close', { bubbles: true, composed: true }))}>
      <div class="share-sheet">
        ${this.renderFresh()}
        ${this.renderTerms()}
        ${this.error ? html`<p class="share-error">${this.error}</p>` : nothing}
        <div class="share-actions">
          ${button({ label: t(this.audience === 'link' ? 'dashboards.shareMakeLink' : 'dashboards.shareSendInvite'), icon: 'share-2', color: 'blue', onClick: () => void this.create(), disabled: this.busy || !this.board })}
        </div>
        <h4 class="share-existing-title">${t('dashboards.shareExisting')}</h4>
        ${this.renderExisting()}
      </div>
    </vitesse-dialog>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-dashboard-share-dialog': VitesseDashboardShareDialog;
  }
}
